import { ListResponse } from '../Shipment/ListResponseDto'

/**
 * DTOs del flujo de importación asíncrona de guías.
 * Backend: /api/shipment/bulk-upload/* — envelope { code, error, message, data, error_data }.
 * Ver Frontend Spec — Shipment Import (secciones 4.2–4.10).
 *
 * Los estados se mapean por `status_name` (estable) en src/utils/shipmentBulkUploadStatus.ts;
 * los IDs numéricos (68–78) son el fallback cuando el backend no manda `status_name`.
 */

/** Estados del header — `status_name` real de [config].[Status] (68–72). */
export const ImportHeaderStatusName = {
  InProcess: 'ShipmentBulkUploadHeader-InProcess',
  Finalized: 'ShipmentBulkUploadHeader-Finalized',
  FinalizedWithErrors: 'ShipmentBulkUploadHeader-FinalizedWithErrors',
  Cancelled: 'ShipmentBulkUploadHeader-Cancelled',
  Deleted: 'ShipmentBulkUploadHeader-Deleted',
} as const
export type ImportHeaderStatusName =
  (typeof ImportHeaderStatusName)[keyof typeof ImportHeaderStatusName]

/**
 * Estados del detalle — `status_name` real de [config].[Status] (73–79).
 * Ojo: 77/78/79 no siguen la convención `ShipmentBulkUploadDetail-*` en la BD
 * (quedaron como texto libre); se replican tal cual para que el match por nombre funcione.
 */
export const ImportDetailStatusName = {
  Pending: 'ShipmentBulkUploadDetail-Pending',
  Executed: 'ShipmentBulkUploadDetail-Executed',
  Error: 'ShipmentBulkUploadDetail-Error',
  Substituted: 'ShipmentBulkUploadDetail-Substituted',
  ErrorProcessing: 'Error Procesando',
  ErrorServer: 'Error Procesando servidor',
  Executing: 'En ejecucion',
} as const
export type ImportDetailStatusName =
  (typeof ImportDetailStatusName)[keyof typeof ImportDetailStatusName]

/** IDs numéricos de [config].[Status] (fallback si el backend no manda `status_name`). */
export const ImportStatusId = {
  HeaderInProcess: 68,
  HeaderFinalized: 69,
  HeaderFinalizedWithErrors: 70,
  HeaderCancelled: 71,
  HeaderDeleted: 72,
  DetailPending: 73,
  DetailExecuted: 74,
  DetailError: 75,
  DetailSubstituted: 76,
  DetailErrorProcessing: 77,
  DetailErrorServer: 78,
  DetailExecuting: 79,
} as const

export interface ImportLogDTO {
  shipment_bulk_upload_log_id: number
  event_type: 'WARNING' | 'ERROR' | string
  message: string
  log_date: string
  creation_user: string | null
  creation_user_name: string | null
  shipment_bulk_upload_detail_id: string | null
}

export interface ImportDetailDTO {
  shipment_bulk_upload_detail_id: string
  line_number: number
  status_id: number
  status_name: string
  /** ShipmentInsertRequestDTO serializado (string JSON). */
  payload_json: string
  /** ID de la guía real creada en [shipping].[ShipmentHeader] (si Executed). */
  shipment_header_id: string | null
  /** Número de guía TEALCA legible (si Executed). */
  shipment_number: string | null
  shipment_header_reference: string | null
  external_manifest: string | null
  error_message: string | null
  /** Nº de reintentos del backend para errores de servidor (status 78). Se muestra "intento N/40". */
  retry_count?: number
  executed_date: string | null
  creation_date: string
  update_date: string | null
}

/** Item del listado (GET /list → results[]). */
export interface ImportHeaderListItemDTO {
  shipment_bulk_upload_header_id: string
  bulk_upload_number: string
  status_id: number
  status_name: string
  start_date: string
  end_date: string | null
  total_details: number
  source_file_name: string | null
  source_file_uploaded: boolean
  source_reason: string | null
  creation_user: string
  creation_user_name: string
  creation_date: string
  billing_account: number | null
  account_id: string | null
  billing_account_name: string | null
  account_name: string | null

  // --- Manifiesto de despacho ---
  // Los tres los devuelven TANTO `GET /shipment-bulk/list` COMO
  // `GET /shipment-bulk/{id}`, por eso viven acá y no en `ImportHeaderDTO`: el
  // listado los necesita para su columna y su atajo por fila.
  //
  // Vienen los TRES en null mientras la carga no tenga manifiesto.

  /**
   * ID del manifiesto en `[logistics].[MasterShipmentHeader]`.
   *
   * **Es el campo que dice si la carga tiene manifiesto**: la condición es
   * `manifest_id !== null`. No existe un `has_manifest` booleano — se eliminó
   * del contrato justamente por ser redundante con esto. Usar `hasManifest()`
   * en vez de comparar a mano.
   */
  manifest_id: string | null
  /**
   * Nº legible del manifiesto (ej. "MAN-0000123"), para mostrar en pantalla y
   * para el botón "Ver manifiesto": `DowloadDispatch` arma `?ManifestCode=${code}`.
   */
  manifest_code: string | null
  /**
   * Cuándo se vinculó el manifiesto.
   *
   * Llega SIN sufijo de zona (`"2026-05-28T12:30:00"`), así que `new Date()` lo
   * interpreta como hora LOCAL, no UTC. Es lo que se quiere acá: el backend manda
   * hora de Venezuela y el operador la lee en su propio huso.
   */
  manifest_create_date: string | null
}

/**
 * ¿La carga ya tiene manifiesto?
 *
 * Una sola definición para que nadie vuelva a inventarse un booleano ni compare
 * contra el campo equivocado. El contrato es explícito: `manifest_id !== null`.
 */
export const hasManifest = (
  header?: Pick<ImportHeaderListItemDTO, 'manifest_id'> | null
): boolean => !!header?.manifest_id

/** Body de POST /shipment-bulk/<id>/manifest. */
export interface BulkUploadManifestRequestDTO {
  /** GUID del manifiesto. El backend lo acepta en mayúsculas o minúsculas. */
  manifest_id: string
}

/** data de POST /shipment-bulk/<id>/manifest. */
export interface BulkUploadManifestResponseDTO {
  shipment_bulk_upload_header_id: string
  manifest_id: string
  manifest_code: string | null
  manifest_create_date: string | null
}

/** Detalle completo (GET /<id>). */
export interface ImportHeaderDTO extends ImportHeaderListItemDTO {
  source_file_path: string | null
  source_file_size: number | null
  source_file_content_type: string | null
  update_user: string | null
  update_user_name: string | null
  update_date: string | null
  details: ImportDetailDTO[]
  logs: ImportLogDTO[]
}

/** Progreso (GET /<id>/progress) — endpoint del polling. */
export interface ImportProgressDTO {
  shipment_bulk_upload_header_id: string
  bulk_upload_number: string
  status_id: number
  status_name: string
  start_date: string
  end_date: string | null
  total_details: number
  executed_count: number
  error_count: number
  pending_count: number
  progress_percentage: number
  is_complete: boolean
}

/** Respuesta de creación (POST /). Solo devuelve los IDs del dueño, no los nombres. */
export interface ImportCreateResponseDTO {
  shipment_bulk_upload_header_id: string
  bulk_upload_number: string
  status_id: number
  source_file_uploaded: boolean
  billing_account: number | null
  account_id: string | null
}

/** URL firmada del archivo (GET /<id>/file). */
export interface ImportFileUrlDTO {
  url: string
  expires_at: string
  file_name: string
  content_type: string
}

/** Página del listado (GET /list). */
export interface ImportListPageDTO {
  count: number
  next: string | null
  previous: string | null
  results: ImportHeaderListItemDTO[]
}

/** Filtros del listado. */
export interface ImportListParams {
  page?: number
  page_size?: number
  start_date_from?: string
  start_date_to?: string
  bulk_upload_number?: string
  status_id?: number
  // Filtros por dueño (match exacto, independientes entre sí). Opcionales.
  billing_account?: string
  account_id?: string
}

/** DTO de creación (payload). La shape de cada item es la misma de POST /api/shipment/. */
export interface ImportInsertRequestDTO {
  bulk_upload_number?: string | null
  source_reason?: string | null
  external_manifest?: string | null
  billing_account?: string | null
  account_id?: string | null
  shipment_list: any[]
}

/**
 * Una corrección de una línea en error (PUT /<id>/update).
 * Actualiza el detalle en su sitio (75/77 Error de dato → 73 Pending, se limpia el error
 * y se reprocesa); NO agrega una línea nueva (eso es lo que hacía `shipment_list`).
 */
export interface ImportCorrectionDTO {
  /** ID del detalle en error de dato (status 75 o 77) que se corrige. */
  shipment_bulk_upload_detail_id: string
  /** Payload corregido completo de la guía (misma shape que un item de shipment_list). */
  shipment: any
}

/** Body de corrección (PUT /<id>/update). */
export interface ImportUpdateRequestDTO {
  corrections: ImportCorrectionDTO[]
}

/**
 * Body de POST /api/shipment/existing — chequeo de guías ya cargadas.
 * `shipment_numbers` se compara (case-insensitive) contra ShipmentNumber y
 * ShipmentHeaderReference. El backend normaliza: recorta espacios, descarta
 * vacíos/blancos y colapsa duplicados. Máx. 5000 tras normalizar.
 * account_site_code / account_id acotan la referencia aliada por cliente (opcionales);
 * omitirlos = prevención de duplicados global.
 */
export interface ShipmentExistingRequestDTO {
  shipment_numbers: string[]
  account_site_code?: string
  account_id?: string
}

/** data de POST /api/shipment/existing — solo las guías que existen (casing almacenado). */
export interface ShipmentExistingResponseDTO {
  existing: string[]
}

/**
 * Resultado de servicio que preserva el `code` del envelope para mapear mensajes UX.
 * Extiende ListResponse para encajar con el resto del sistema.
 */
export interface ImportApiResult<T> extends ListResponse<T> {
  /** code del envelope; '0' en éxito, ej "shipping.ShipmentBulkUploadHeader.7" en error. */
  code: string
  /** error_data del envelope (contexto adicional de validación, etc.). */
  errorData?: Record<string, any>
  /**
   * Status HTTP, SOLO cuando la respuesta no traía envelope (404 de ruta, 405 de
   * método, 502 del proxy). Con envelope el status siempre es 200 y no dice nada.
   * Sirve para distinguir "el endpoint todavía no existe" de "el backend dijo que no".
   */
  httpStatus?: number
}
